import { useEffect } from "react";
import { Link } from "react-router-dom";
import AnimatedBackground from "../../common/AnimatedBackground";
import SEOHead from "../../common/SEOHead";
import Footer from "../Footer/Footer";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="relative w-full min-h-lvh text-white flex flex-col">
      <SEOHead
        title="Política de Privacidad"
        description="Conocé cómo se gestionan los datos personales y el uso de cookies en martinofranco.com."
        url="/privacy-policy"
      />
      <AnimatedBackground />

      <main className="relative flex-1 max-w-4xl mx-auto w-full px-6 sm:px-12 py-10">
        <h1 className="text-3xl sm:text-4xl font-bold font-header text-white mb-3">
          Política de Privacidad
        </h1>
        <p className="text-sm text-gray-500 mb-8">
          Última actualización: 9 de abril de 2026.
        </p>

        <div className="h-px bg-gray-700/60 mb-8" />

        <div className="prose-blog text-gray-300 leading-relaxed">
          <p>
            En martinofranco.com se respeta la privacidad de los usuarios. Esta
            política explica qué información se recopila, para qué se usa y qué
            opciones tenés sobre tus datos. El uso del sitio implica la aceptación
            de las prácticas descritas en este documento.
          </p>

          <h2>1. Responsable del sitio</h2>
          <p>
            El responsable de este sitio es Franco Martino, desarrollador de
            software con domicilio en Buenos Aires, Argentina. Si necesitás hacer
            una consulta sobre privacidad, podés escribir a través de la sección
            de contacto del sitio y se responderá a la brevedad.
          </p>

          <h2>2. Datos que se pueden recopilar</h2>
          <p>Durante la navegación del sitio, se pueden recopilar los siguientes tipos de información:</p>
          <ul>
            <li>
              <strong>Datos enviados voluntariamente:</strong> información que los usuarios proporcionan al completar formularios de contacto (nombre, dirección de email y mensaje). Estos datos se envían exclusivamente con el consentimiento del usuario.
            </li>
            <li>
              <strong>Datos técnicos de navegación:</strong> información anónima como tipo de dispositivo, navegador utilizado, páginas visitadas, tiempo de sesión y dirección IP anonimizada. Estos datos se recogen con fines estadísticos y de mejora del servicio.
            </li>
            <li>
              <strong>Cookies publicitarias y de medición:</strong> cuando el usuario otorga su consentimiento, se pueden utilizar cookies de terceros (como Google AdSense) para mostrar anuncios personalizados y medir la efectividad de las campañas publicitarias.
            </li>
          </ul>

          <h2>3. Finalidad del tratamiento</h2>
          <p>Los datos recopilados se utilizan exclusivamente para las siguientes finalidades:</p>
          <ul>
            <li>Responder consultas y mensajes enviados por los visitantes a través del formulario de contacto.</li>
            <li>Mejorar el contenido, la estructura y la experiencia de uso del sitio mediante análisis de navegación agregados.</li>
            <li>
              Mostrar anuncios relevantes mediante Google AdSense, según las políticas de consentimiento aplicables y la normativa vigente.
            </li>
            <li>Mantener la seguridad del sitio y prevenir usos abusivos o fraudulentos.</li>
          </ul>
          <p>No se utilizan los datos para toma de decisiones automatizadas que produzcan efectos jurídicos sobre los usuarios.</p>

          <h2>4. Cookies y publicidad</h2>
          <p>
            Este sitio puede utilizar cookies propias y de terceros para analizar tráfico y mostrar anuncios. Google, como proveedor externo, utiliza cookies para publicar anuncios basados en visitas previas del usuario a este y otros sitios web.
          </p>
          <p>
            Los usuarios pueden desactivar el uso de cookies para publicidad personalizada accediendo a la configuración de anuncios de Google. Asimismo, pueden configurar su navegador para rechazar todas las cookies o para que les avise cuando se envía una cookie.
          </p>
          <p>
            Si no se aceptan las cookies, el sitio seguirá siendo funcional aunque algunas funcionalidades relacionadas con la personalización de anuncios pueden no estar disponibles.
          </p>
          <p>
            Podés obtener más información sobre el uso de datos en productos de Google en su política de privacidad y en su configuración de anuncios.
          </p>

          <h2>5. Base legal del tratamiento</h2>
          <p>
            El tratamiento de datos personales se realiza sobre la base del consentimiento del usuario (para formularios de contacto y cookies no esenciales) y del interés legítimo del responsable (para análisis estadístico anonimizado y seguridad del sitio).
          </p>

          <h2>6. Compartición de datos</h2>
          <p>
            No se venden, alquilan ni ceden datos personales a terceros sin consentimiento explícito. Los datos solo pueden compartirse con servicios necesarios para operar el sitio (por ejemplo, proveedores de hosting, servicios de formularios como Web3Forms, y herramientas de analítica), bajo sus respectivas políticas de privacidad y acuerdos de confidencialidad.
          </p>

          <h2>7. Conservación de datos</h2>
          <p>
            Los datos se conservan durante el tiempo necesario para cumplir con la finalidad para la que fueron recopilados o hasta que el usuario solicite su eliminación, cuando sea legalmente posible. Los datos de contacto se conservan por un máximo de 12 meses después de la última interacción, salvo que exista un requerimiento legal que exija un período diferente.
          </p>

          <h2>8. Derechos del usuario</h2>
          <p>
            De acuerdo con la normativa aplicable, los usuarios tienen derecho a:
          </p>
          <ul>
            <li><strong>Acceso:</strong> solicitar confirmación sobre si se están tratando datos personales y acceder a ellos.</li>
            <li><strong>Rectificación:</strong> solicitar la corrección de datos inexactos o incompletos.</li>
            <li><strong>Eliminación:</strong> solicitar la supresión de los datos cuando ya no sean necesarios.</li>
            <li><strong>Oposición:</strong> oponerse al tratamiento de sus datos para fines de marketing o análisis.</li>
            <li><strong>Portabilidad:</strong> recibir los datos en un formato estructurado y legible.</li>
          </ul>
          <p>Para ejercer cualquiera de estos derechos, contactate por los medios publicados en la sección de contacto del sitio. Se responderá a la solicitud dentro de los plazos legales establecidos.</p>

          <h2>9. Cambios en esta política</h2>
          <p>
            Esta política puede actualizarse para reflejar cambios normativos, técnicos o de funcionamiento del sitio. Se recomienda revisar periódicamente esta página para estar al tanto de cualquier modificación. La fecha de actualización siempre se indicará al inicio de esta página y los cambios entrarán en vigor inmediatamente después de su publicación.
          </p>
        </div>

        <div className="mt-10 flex justify-center">
          <Link
            to="/"
            className="text-gray-500 hover:text-white text-sm transition-colors duration-200 underline underline-offset-4"
          >
            ← Volver al portfolio
          </Link>
        </div>
      </main>

      <Footer />
    </div>
  );
}